/*
 * Copyright (C) 2015 Spanning Cloud Apps LLC.  All rights reserved.
 */
/**
 * Basic Batch store implementation of the PickerStore.
 */
'use strict';

angular
  .module('stores.picker.basicbatch', ['stores.picker.batch'])
  .factory('BasicBatchPickerStore', function ($filter, $q, BatchPickerStore, batchUtils, NG_DATE_FORMATS) {
    /**
     * The BasicBatchPickerStore.  It will create a picker store specific to a basic batch picker.
     * @constructor
     */
    var BasicBatchPickerStore = function () {
      BatchPickerStore.apply(this, arguments);
      this.nextTooltipText = $filter('i18n')('NEXT_BACKUP');
      this.previousTooltipText = $filter('i18n')('PREVIOUS_BACKUP');
      this.latestLabel = $filter('i18n')('LATEST_BACKUP');
      this.batchObject = {};
      this.lastValidBatch = {};
    };

    BasicBatchPickerStore.prototype = new BatchPickerStore();
    BasicBatchPickerStore.prototype.name = 'BasicBatchPickerStore';

    /**
     * Utility function to get the latest batch on a particular date.
     *
     * @param {Date} batchDate - The date to specify what day to search.
     * @returns {Promise} - A Promise that resolves into the latest batch
     *   on the date provided as well as updating the currentBatch value.
     *   Returns undefined if there is no batch for the date.
     */
    BasicBatchPickerStore.prototype.getBatchByDate = function (batchDate) {
      var me = this;
      if (batchDate && !moment(me.batchObject.date).clone().startOf('day').isSame(batchDate)) {
        return batchUtils.getBatchByDate(batchDate).then(function (batch) {
          if (batch) {
            me.batchObject = batch;
          } else {
            me.lastValidBatch = angular.copy(me.batchObject);
            me.batchObject = {
              noBackup: true,
              date: batchDate
            };
          }
          return batch;
        });
      }
    };

    /**
     * Utility for getting the next batch after the provided batch id.
     *
     * @returns {Promise} - A Promise that resolves into the next batch
     *   as well as updating the currentBatch value.  Returns undefined if
     *   there is no next batch.
     */
    BasicBatchPickerStore.prototype.getNextBatch = function () {
      var me = this;
      if (_.isNumber(this.batchObject.batchId)) {
        return batchUtils.getNextBatch(this.batchObject.batchId).then(function (batch) {
          if (batch) {
            me.batchObject = batch;
          }
          return me.batchObject;
        });
      }
    };

    /**
     * Utility for getting the previous batch before the provided batch id.
     *
     * @params {Number} batchId - The id of the batch to retrieve.
     *
     * @returns {Promise} - A Promise that resolves into the previous batch
     *   as well as updating the currentBatch value.  Returns undefined if
     *   there is no previous batch.
     */
    BasicBatchPickerStore.prototype.getPreviousBatch = function (batchId) {
      var me = this,
        startBatchId = batchId || this.batchObject.batchId;
      if (_.isNumber(startBatchId)) {
        return batchUtils.getPreviousBatch(startBatchId).then(function (batch) {
          if (batch) {
            me.batchObject = batch;
          }
          return me.batchObject;
        });
      }
    };

    /**
     * Get the current id of the loaded batch.
     * @returns {Number} - The id.
     */
    BasicBatchPickerStore.prototype.getCurrentId = function () {
      return this.batchObject.batchId;
    };

    /**
     * Utility for getting the batch with the provided batch id.
     *
     * @params {Number} batchId - The id of the batch to retrieve.
     *
     * @returns {Promise} - A Promise that resolves into the batch
     *   as well as updating the currentBatch value.  Returns undefined if
     *   there is no previous batch.
     */
    BasicBatchPickerStore.prototype.getBatch = function (batchId) {
      var me = this,
        batchIdToGet = batchId || this.batchObject.batchId;
      if (_.isNumber(batchIdToGet)) {
        return batchUtils.loadBatch(batchIdToGet).then(function (batch) {
          if (batch) {
            me.batchObject = batch;
          }
          return me.batchObject;
        });
      } else {
        return $q.when();
      }
    };

    // Override all necessary functions from the PickerStore.
    /**
     * @overrides {@link PickerStore.getNext}
     */
    BasicBatchPickerStore.prototype.getNext = function () {
      var currentId = this.getCurrentId();
      if (this.details && _.isNumber(currentId) && currentId !== this.details.backup.last.id) {
        return this.getNextBatch();
      }
    };

    /**
     * @overrides {@link PickerStore.getPrevious}
     */
    BasicBatchPickerStore.prototype.getPrevious = function (batchId) {
      var currentId = batchId || this.getCurrentId();
      if (this.details && _.isNumber(currentId) && currentId !== this.details.backup.first.id) {
        return this.getPreviousBatch(currentId);
      }
    };

    /**
     * @overrides {@link PickerStore.getLatest}
     */
    BasicBatchPickerStore.prototype.getLatest = function () {
      var me = this;
      return batchUtils.getLatestBackup().then(function (batch) {
        if (batch) {
          me.batchObject = batch;
        }
        return batch;
      });
    };

    /**
     * @overrides {@link PickerStore.getByDate}
     */
    BasicBatchPickerStore.prototype.getByDate = function (dateOfInterest) {
      return this.getBatchByDate(dateOfInterest);
    };

    /**
     * @overrides {@link PickerStore.previousDisabled}
     */
    BasicBatchPickerStore.prototype.previousDisabled = function () {
      return this.details && this.batchObject.batchId === this.details.backup.first.id;
    };

    /**
     * @overrides {@link PickerStore.nextDisabled}
     */
    BasicBatchPickerStore.prototype.nextDisabled = function () {
      return this.details && this.batchObject.batchId === this.details.backup.last.id;
    };

    /**
     * @overrides {@link PickerStore.setupDateTimeListener}
     */
    BasicBatchPickerStore.prototype.setupDateTimeListener = function ($scope) {
      var me = this;
      $scope.$watch(
        function () {
          return me.batchObject;
        },
        function () {
          if (me.batchObject.noBackup) {
            me.batchObject = me.lastValidBatch;
            if ($scope.invalidDate) {
              $scope.invalidDate();
            }
          } else {
            $scope.dt = me.batchObject.date;
            $scope.format = NG_DATE_FORMATS.LONG;
          }
        },
        true
      );
    };

    return BasicBatchPickerStore;
  });
